import React, { useState } from "react";
import { Grid, Table, Tag } from "antd";
import { useSelector } from "react-redux";
import commaNumber from "comma-number";
import "../../../../components/common/bt.css";

const format = commaNumber.bindWith(",", ".");
const fmt = (val) => format(parseFloat(val || 0).toFixed(2));

/**
 * AgeingTable - renders for both Customer and Vendor ageing
 * Props:
 *   mode: "customer" | "vendor"
 *   printMode: boolean
 */
const AgeingTable = ({ mode = "customer", printMode = false }) => {
  const {
    customerList,
    customerLoading,
    customerTotalBalance,
    vendorList,
    vendorLoading,
    vendorTotalBalance,
  } = useSelector((state) => state.ageing);

  const isCustomer = mode === "customer";
  const list = isCustomer ? customerList : vendorList;
  const loading = isCustomer ? customerLoading : vendorLoading;
  const totalBalance = isCustomer ? customerTotalBalance : vendorTotalBalance;

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const { useBreakpoint } = Grid;  
      
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Colour-code bucket cells by severity
  const bucketRender = (val, bucket) => {
    const n = parseFloat(val || 0);
    if (n <= 0) return <span style={{ color: "#999" }}>—</span>;

    // Escalating colours: green → yellow → orange → red
    const colors = {
      current:   "#52c41a",
      days31_60: "#faad14",
      days61_90: "#fa8c16",
      days90plus:"#f5222d",
    };
    return (
      <Tag
        color={colors[bucket]}
        style={{ fontWeight: 600, minWidth: 80, textAlign: "right" }}
      >
        {fmt(n)}
      </Tag>
    );
  };

  const baseColumns = [
    { title: "No.", render: (_, __, i) => i + 1, width: 55, fixed: !isMobile && "left" },
    ...(isCustomer
      ? [{ title: "Code", dataIndex: "acc_code", width: 90 }]
      : []),
    {
      title: isCustomer ? "Customer Name" : "Vendor Name",
      dataIndex: "acc_name",
      fixed: !isMobile && "left",
      width: 180,
    },
    { title: "Contact", dataIndex: "contact_no", width: 120 },
    { title: "Address",  dataIndex: "address",    width: 160, ellipsis: true },
    {
      title: "Opening",
      dataIndex: "opening_balance",
      width: 110,
      align: "right",
      render: fmt,
    },
    {
      title: "Current (0–30d)",
      dataIndex: "current",
      width: 130,
      align: "right",
      render: (v) => bucketRender(v, "current"),
    },
    {
      title: "31–60 Days",
      dataIndex: "days31_60",
      width: 120,
      align: "right",
      render: (v) => bucketRender(v, "days31_60"),
    },
    {
      title: "61–90 Days",
      dataIndex: "days61_90",
      width: 120,
      align: "right",
      render: (v) => bucketRender(v, "days61_90"),
    },
    {
      title: "90+ Days",
      dataIndex: "days90plus",
      width: 120,
      align: "right",
      render: (v) => bucketRender(v, "days90plus"),
    },
    {
      title: "Total Balance",
      dataIndex: "balance",
      width: 130,
      align: "right",
      fixed: !isMobile && "right",
      render: (val) => {
        const n = parseFloat(val || 0);
        return (
          <strong style={{ color: n > 0 ? "#f5222d" : n < 0 ? "#52c41a" : "inherit" }}>
            {fmt(n)}
          </strong>
        );
      },
    },
  ];

  // Grand total footer
  const totals = list.reduce(
    (acc, row) => ({
      opening:  acc.opening  + parseFloat(row.opening_balance || 0),
      current:  acc.current  + parseFloat(row.current         || 0),
      d31_60:   acc.d31_60   + parseFloat(row.days31_60       || 0),
      d61_90:   acc.d61_90   + parseFloat(row.days61_90       || 0),
      d90plus:  acc.d90plus  + parseFloat(row.days90plus      || 0),
    }),
    { opening: 0, current: 0, d31_60: 0, d61_90: 0, d90plus: 0 }
  );

  const colSpanName = isCustomer ? 5 : 4; // No., Code(customer only), Name, Contact, Address

  if (printMode) {
    return (
      <table className="ledger-print-table">
        <colgroup>
          <col style={{ width: "4%" }} />
          {isCustomer && <col style={{ width: "8%" }} />}
          <col style={{ width: isCustomer ? "18%" : "22%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "14%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: isCustomer ? "9%" : "9%" }} />
        </colgroup>
        <tbody>
          <tr className="ledger-print-heading">
            <td>No.</td>
            {isCustomer && <td>Code</td>}
            <td>{isCustomer ? "Customer Name" : "Vendor Name"}</td>
            <td>Contact</td>
            <td>Address</td>
            <td style={{ textAlign: "right" }}>Opening</td>
            <td style={{ textAlign: "right" }}>0-30 Days</td>
            <td style={{ textAlign: "right" }}>31-60 Days</td>
            <td style={{ textAlign: "right" }}>61-90 Days</td>
            <td style={{ textAlign: "right" }}>90+ Days</td>
            <td style={{ textAlign: "right" }}>Total Balance</td>
          </tr>

          {list.map((row, index) => (
            <tr key={`${row.acc_id}-${index}`}>
              <td>{index + 1}</td>
              {isCustomer && <td>{row.acc_code}</td>}
              <td>{row.acc_name}</td>
              <td>{row.contact_no}</td>
              <td>{row.address}</td>
              <td style={{ textAlign: "right" }}>{fmt(row.opening_balance)}</td>
              <td style={{ textAlign: "right" }}>{fmt(row.current)}</td>
              <td style={{ textAlign: "right" }}>{fmt(row.days31_60)}</td>
              <td style={{ textAlign: "right" }}>{fmt(row.days61_90)}</td>
              <td style={{ textAlign: "right" }}>{fmt(row.days90plus)}</td>
              <td style={{ textAlign: "right" }}>{fmt(row.balance)}</td>
            </tr>
          ))}

          <tr className="ledger-total-row">
            <td colSpan={colSpanName} style={{ textAlign: "right" }}>
              Grand Total
            </td>
            <td style={{ textAlign: "right" }}>{fmt(totals.opening)}</td>
            <td style={{ textAlign: "right" }}>{fmt(totals.current)}</td>
            <td style={{ textAlign: "right" }}>{fmt(totals.d31_60)}</td>
            <td style={{ textAlign: "right" }}>{fmt(totals.d61_90)}</td>
            <td style={{ textAlign: "right" }}>{fmt(totals.d90plus)}</td>
            <td style={{ textAlign: "right" }}>{fmt(totalBalance)}</td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <Table
      rowKey={(r, i) => `${r.acc_id}-${i}`}
      columns={baseColumns}
      dataSource={list}
      loading={loading}
      bordered
      size={printMode ? "small" : "middle"}
      scroll={printMode ? undefined : { x: "max-content" }}
      pagination={
        printMode
          ? false
          : {
              ...pagination,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total) => `${total} records`,
            }
      }
      onChange={(p) => setPagination(p)}
      summary={() => (
        <Table.Summary.Row style={{ background: "#fafafa", fontWeight: 700 }}>
          <Table.Summary.Cell index={0} colSpan={colSpanName}>
            Grand Total
          </Table.Summary.Cell>
          <Table.Summary.Cell align="right">{fmt(totals.opening)}</Table.Summary.Cell>
          <Table.Summary.Cell align="right">{fmt(totals.current)}</Table.Summary.Cell>
          <Table.Summary.Cell align="right">{fmt(totals.d31_60)}</Table.Summary.Cell>
          <Table.Summary.Cell align="right">{fmt(totals.d61_90)}</Table.Summary.Cell>
          <Table.Summary.Cell align="right">{fmt(totals.d90plus)}</Table.Summary.Cell>
          <Table.Summary.Cell align="right">
            <span style={{ color: "#f5222d" }}>{fmt(totalBalance)}</span>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      )}
    />
  );
};

export default AgeingTable;